// AI Recording Studio Pro — Video Editor (CapCut-style)
//
// Lot 1 — "Timeline pro": real preview engine (wall-clock playhead driving one <video>
// per source), filmstrip thumbnails, audio waveforms, snapping, drag & drop from the
// media library, undo/redo, copy/paste, multi-selection and playhead-following scroll.
//
// Export renders the timeline server-side with ffmpeg (concat/xfade + drawtext + amix).
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Play, Pause, SkipBack, SkipForward, Scissors, Trash2, Copy, Type,
  ZoomIn, ZoomOut, Upload, Film, Music, Volume2, VolumeX, Download,
  Video as VideoIcon, Maximize2, Undo2, Redo2, Magnet, AudioLines, Captions,
} from "lucide-react";
import { toast } from "sonner";
import { studioRenderAPI, studioTranscribeAPI } from "@/lib/api";

const MIN_PX_PER_SEC = 8;
const MAX_PX_PER_SEC = 400;
const TRACK_HEADER_W = 116;
const SNAP_PX = 8;
const THUMB_W = 64;
const MAX_THUMBS = 40;
const HISTORY_LIMIT = 60;

// Names match ffmpeg's xfade transitions so the preview and the render agree
const TRANSITIONS = [
  { v: "none", l: "Aucune" }, { v: "fade", l: "Fondu" }, { v: "dissolve", l: "Dissolution" },
  { v: "wipeleft", l: "Balayage ←" }, { v: "wiperight", l: "Balayage →" },
  { v: "slideleft", l: "Glissement ←" }, { v: "circleopen", l: "Cercle" },
];

const FORMATS = {
  source: { label: "Source", dims: null, css: null },
  "16:9": { label: "16:9 YouTube", dims: [1920, 1080], css: "16 / 9" },
  "9:16": { label: "9:16 Shorts", dims: [1080, 1920], css: "9 / 16" },
  "1:1": { label: "1:1 Carré", dims: [1080, 1080], css: "1 / 1" },
};

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 9)}`;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function formatTime(s) {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const cs = Math.floor((s % 1) * 100);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}

function probeDuration(url, kind) {
  return new Promise((resolve, reject) => {
    const el = document.createElement(kind === "audio" ? "audio" : "video");
    el.preload = "metadata";
    el.onloadedmetadata = () => resolve(el.duration);
    el.onerror = () => reject(new Error("Média illisible"));
    el.src = url;
  });
}

// Grab evenly spaced frames from a video to build the filmstrip
async function extractThumbnails(url, duration) {
  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.src = url;
  await new Promise((res, rej) => {
    video.onloadeddata = res;
    video.onerror = () => rej(new Error("thumbnail decode"));
  });

  const count = clamp(Math.ceil(duration / 2), 2, MAX_THUMBS);
  const canvas = document.createElement("canvas");
  canvas.width = 160;
  canvas.height = 90;
  const ctx = canvas.getContext("2d");
  const out = [];

  for (let i = 0; i < count; i++) {
    const t = (duration * i) / count;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((res) => {
      video.onseeked = res;
      video.currentTime = Math.min(t, Math.max(0, duration - 0.05));
    });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    out.push({ t, url: canvas.toDataURL("image/jpeg", 0.5) });
  }
  return out;
}

// Decode an audio file into per-bucket peaks for the waveform
async function extractPeaks(url, buckets = 900) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  try {
    const buf = await fetch(url).then((r) => r.arrayBuffer());
    const audio = await ctx.decodeAudioData(buf);
    const data = audio.getChannelData(0);
    const step = Math.max(1, Math.floor(data.length / buckets));
    const peaks = [];
    for (let i = 0; i < buckets; i++) {
      let max = 0;
      const start = i * step;
      for (let j = 0; j < step && start + j < data.length; j += 8) {
        const v = Math.abs(data[start + j]);
        if (v > max) max = v;
      }
      peaks.push(max);
    }
    return peaks;
  } catch {
    return null;
  } finally {
    ctx.close();
  }
}

// Defined at module scope: declaring it inside the editor would make React treat it as
// a new component type on every render and remount the canvas, resetting the drawing.
function Waveform({ peaks, width }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !peaks) return;
    const w = Math.max(1, Math.floor(width));
    cv.width = w;
    cv.height = 36;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, w, 36);
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    for (let x = 0; x < w; x++) {
      const p = peaks[Math.floor((x / w) * peaks.length)] || 0;
      const h = Math.max(1, p * 32);
      ctx.fillRect(x, 18 - h / 2, 1, h);
    }
  }, [peaks, width]);
  if (!peaks) return null;
  return <canvas ref={ref} className="absolute inset-0 pointer-events-none" />;
}

const EMPTY_DOC = { videoClips: [], audioClips: [], textClips: [], subtitles: [] };

export default function RecordingVideoEditor() {
  // ── Undoable document ──
  const [doc, setDocState] = useState(EMPTY_DOC);
  const docRef = useRef(doc);
  docRef.current = doc;
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [, setHistoryTick] = useState(0);

  const pushHistory = useCallback(() => {
    pastRef.current.push(docRef.current);
    if (pastRef.current.length > HISTORY_LIMIT) pastRef.current.shift();
    futureRef.current = [];
    setHistoryTick((t) => t + 1);
  }, []);

  // history:true snapshots before mutating; drags snapshot once then patch silently
  const update = useCallback((fn, { history = true } = {}) => {
    if (history) pushHistory();
    setDocState((d) => fn(d));
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    futureRef.current.push(docRef.current);
    setDocState(pastRef.current.pop());
    setHistoryTick((t) => t + 1);
  }, []);

  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    pastRef.current.push(docRef.current);
    setDocState(futureRef.current.pop());
    setHistoryTick((t) => t + 1);
  }, []);

  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;

  // ── Media & UI state ──
  const [sources, setSources] = useState([]);
  const [thumbs, setThumbs] = useState({}); // sourceId -> [{t,url}]
  const [peaks, setPeaks] = useState({});   // sourceId -> number[]
  const [selection, setSelection] = useState([]); // [{track,id}]
  const [isPlaying, setIsPlaying] = useState(false);
  const [displayTime, setDisplayTime] = useState(0);
  const [pxPerSec, setPxPerSec] = useState(60);
  const [masterVolume, setMasterVolume] = useState(100);
  const [snapping, setSnapping] = useState(true);
  const [dropHint, setDropHint] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState("source");
  const [transcribing, setTranscribing] = useState(false);

  const playheadRef = useRef(0);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const videoElsRef = useRef({});
  const audioElsRef = useRef({});
  const activeVideoRef = useRef(null);
  const activeAudioRef = useRef(null);
  const timelineRef = useRef(null);
  const playheadElRef = useRef(null);
  const fileRef = useRef(null);
  const clipboardRef = useRef(null);
  const playerRef = useRef(null);

  const sourceById = useMemo(() => Object.fromEntries(sources.map((s) => [s.id, s])), [sources]);

  // Video clips are contiguous: derive each start from the ones before it
  const videoLayout = useMemo(() => {
    let prevEnd = 0;
    let prevDur = 0;
    return doc.videoClips.map((c, i) => {
      const dur = (c.out - c.in) / c.speed;
      const type = c.transition?.type;
      let trans = 0;
      if (i > 0 && type && type !== "none") {
        trans = clamp(c.transition.dur || 0, 0, Math.min(prevDur - 0.05, dur - 0.05));
        if (trans < 0.05) trans = 0;
      }
      const start = i === 0 ? 0 : Math.max(0, prevEnd - trans);
      prevEnd = start + dur;
      prevDur = dur;
      return { ...c, start, dur, trans };
    });
  }, [doc.videoClips]);

  const totalDuration = useMemo(() => {
    const v = videoLayout.reduce((a, c) => a + c.dur, 0);
    const a = doc.audioClips.reduce((m, c) => Math.max(m, c.start + c.dur), 0);
    const t = doc.textClips.reduce((m, c) => Math.max(m, c.start + c.dur), 0);
    const s2 = (doc.subtitles || []).reduce((m, c) => Math.max(m, c.start + c.dur), 0);
    return Math.max(v, a, t, s2, 0);
  }, [videoLayout, doc.audioClips, doc.textClips, doc.subtitles]);

  // Windows overlap during a transition: the incoming clip wins
  const clipAt = useCallback((t) => {
    let found = null;
    for (const c of videoLayout) if (t >= c.start && t < c.start + c.dur) found = c;
    return found;
  }, [videoLayout]);

  // Preview approximation of the transition: a dip to black. The exported file uses a
  // true xfade — compositing two decoders live would need a WebGL pipeline.
  const transitionDip = useMemo(() => {
    const c = videoLayout.find((x) => x.trans > 0 && displayTime >= x.start && displayTime < x.start + x.trans);
    if (!c) return 0;
    const p = (displayTime - c.start) / c.trans;
    return 1 - Math.abs(2 * p - 1);
  }, [videoLayout, displayTime]);
  const audioAt = useCallback((t) => doc.audioClips.find((c) => t >= c.start && t < c.start + c.dur) || null, [doc.audioClips]);
  const visibleTexts = useMemo(
    () => doc.textClips.filter((c) => displayTime >= c.start && displayTime < c.start + c.dur),
    [doc.textClips, displayTime]
  );

  const visibleSubtitle = useMemo(
    () => (doc.subtitles || []).find((c) => displayTime >= c.start && displayTime < c.start + c.dur) || null,
    [doc.subtitles, displayTime]
  );

  const isSelected = useCallback((id) => selection.some((s) => s.id === id), [selection]);
  const soleSelection = selection.length === 1 ? selection[0] : null;

  // ── Media sync ──
  const syncMedia = useCallback((t, playing) => {
    const clip = clipAt(t);
    Object.entries(videoElsRef.current).forEach(([sid, el]) => {
      if (el && (!clip || sid !== clip.sourceId) && !el.paused) el.pause();
    });
    if (clip) {
      const el = videoElsRef.current[clip.sourceId];
      if (el) {
        const target = clip.in + (t - clip.start) * clip.speed;
        const switched = activeVideoRef.current !== clip.id;
        if (switched || Math.abs(el.currentTime - target) > 0.3) {
          el.currentTime = clamp(target, 0, Math.max(0, el.duration || target));
        }
        activeVideoRef.current = clip.id;
        el.playbackRate = clip.speed;
        el.volume = (clip.muted ? 0 : clip.volume) * (masterVolume / 100);
        if (playing && el.paused) el.play().catch(() => {});
        if (!playing && !el.paused) el.pause();
      }
    } else activeVideoRef.current = null;

    const aClip = audioAt(t);
    Object.entries(audioElsRef.current).forEach(([sid, el]) => {
      if (el && (!aClip || sid !== aClip.sourceId) && !el.paused) el.pause();
    });
    if (aClip) {
      const el = audioElsRef.current[aClip.sourceId];
      if (el) {
        const target = aClip.in + (t - aClip.start);
        const switched = activeAudioRef.current !== aClip.id;
        if (switched || Math.abs(el.currentTime - target) > 0.3) {
          el.currentTime = clamp(target, 0, Math.max(0, el.duration || target));
        }
        activeAudioRef.current = aClip.id;
        el.volume = (aClip.muted ? 0 : aClip.volume) * (masterVolume / 100);
        if (playing && el.paused) el.play().catch(() => {});
        if (!playing && !el.paused) el.pause();
      }
    } else activeAudioRef.current = null;
  }, [clipAt, audioAt, masterVolume]);

  const followPlayhead = useCallback((t) => {
    const el = timelineRef.current;
    if (!el) return;
    const x = t * pxPerSec + TRACK_HEADER_W;
    const left = el.scrollLeft;
    const right = left + el.clientWidth;
    if (x < left + TRACK_HEADER_W + 20) el.scrollLeft = Math.max(0, x - TRACK_HEADER_W - 20);
    else if (x > right - 80) el.scrollLeft = x - el.clientWidth + 160;
  }, [pxPerSec]);

  const paintPlayhead = useCallback((t) => {
    if (playheadElRef.current) playheadElRef.current.style.transform = `translateX(${t * pxPerSec}px)`;
  }, [pxPerSec]);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
    stopLoop();
    Object.values(videoElsRef.current).forEach((el) => el && !el.paused && el.pause());
    Object.values(audioElsRef.current).forEach((el) => el && !el.paused && el.pause());
  }, [stopLoop]);

  const seek = useCallback((t) => {
    const c = clamp(t, 0, totalDuration);
    playheadRef.current = c;
    setDisplayTime(c);
    paintPlayhead(c);
    syncMedia(c, isPlaying);
  }, [totalDuration, paintPlayhead, syncMedia, isPlaying]);

  const play = useCallback(() => {
    if (totalDuration <= 0) return;
    if (playheadRef.current >= totalDuration - 0.02) seek(0);
    setIsPlaying(true);
  }, [totalDuration, seek]);

  useEffect(() => {
    if (!isPlaying) {
      stopLoop();
      syncMedia(playheadRef.current, false);
      return;
    }
    lastTsRef.current = performance.now();
    const tick = (now) => {
      const dt = (now - lastTsRef.current) / 1000;
      lastTsRef.current = now;
      let t = playheadRef.current + dt;
      if (t >= totalDuration) {
        playheadRef.current = totalDuration;
        paintPlayhead(totalDuration);
        setDisplayTime(totalDuration);
        pause();
        return;
      }
      playheadRef.current = t;
      paintPlayhead(t);
      setDisplayTime(t);
      followPlayhead(t);
      syncMedia(t, true);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return stopLoop;
  }, [isPlaying, totalDuration, syncMedia, paintPlayhead, followPlayhead, pause, stopLoop]);

  useEffect(() => paintPlayhead(playheadRef.current), [pxPerSec, paintPlayhead]);

  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  useEffect(() => () => sourcesRef.current.forEach((s) => URL.revokeObjectURL(s.url)), []);

  // ── Import ──
  const handleFiles = async (fileList) => {
    for (const file of Array.from(fileList || [])) {
      const isAudio = file.type.startsWith("audio/");
      const url = URL.createObjectURL(file);
      let duration;
      try {
        duration = await probeDuration(url, isAudio ? "audio" : "video");
      } catch {
        URL.revokeObjectURL(url);
        toast.error(`Impossible de lire ${file.name}`);
        continue;
      }
      const source = { id: uid("src"), name: file.name, url, file, kind: isAudio ? "audio" : "video", duration };
      setSources((p) => [...p, source]);

      if (isAudio) {
        update((d) => ({
          ...d,
          audioClips: [...d.audioClips, { id: uid("a"), sourceId: source.id, in: 0, start: playheadRef.current, dur: duration, volume: 1, muted: false }],
        }));
        extractPeaks(url).then((pk) => pk && setPeaks((p) => ({ ...p, [source.id]: pk })));
      } else {
        update((d) => ({
          ...d,
          videoClips: [...d.videoClips, { id: uid("v"), sourceId: source.id, in: 0, out: duration, speed: 1, volume: 1, muted: false }],
        }));
        extractThumbnails(url, duration)
          .then((th) => setThumbs((p) => ({ ...p, [source.id]: th })))
          .catch(() => {});
      }
      toast.success(`${file.name} importé`);
    }
  };

  // ── Snapping ──
  const snapTargets = useCallback((excludeId) => {
    const arr = [0, playheadRef.current, totalDuration];
    videoLayout.forEach((c) => { arr.push(c.start, c.start + c.dur); });
    doc.audioClips.forEach((c) => { if (c.id !== excludeId) arr.push(c.start, c.start + c.dur); });
    doc.textClips.forEach((c) => { if (c.id !== excludeId) arr.push(c.start, c.start + c.dur); });
    return arr;
  }, [videoLayout, doc.audioClips, doc.textClips, totalDuration]);

  const snapSec = useCallback((t, excludeId) => {
    if (!snapping) return t;
    const tol = SNAP_PX / pxPerSec;
    let best = t;
    let bestD = tol;
    for (const s of snapTargets(excludeId)) {
      const d = Math.abs(s - t);
      if (d < bestD) { bestD = d; best = s; }
    }
    return best;
  }, [snapping, pxPerSec, snapTargets]);

  // ── Detach a video clip's audio onto the audio track ──
  const detachAudio = (clip) => {
    if (clip.speed !== 1) {
      return toast.error("Remettez la vitesse à 1x avant de détacher l'audio");
    }
    const laid = videoLayout.find((c) => c.id === clip.id);
    if (!laid) return;
    update((d) => ({
      ...d,
      videoClips: d.videoClips.map((c) => (c.id === clip.id ? { ...c, muted: true } : c)),
      audioClips: [...d.audioClips, {
        id: uid("a"), sourceId: clip.sourceId, in: clip.in,
        start: laid.start, dur: clip.out - clip.in,
        volume: clip.volume, muted: false,
      }],
    }));
    if (!peaks[clip.sourceId]) {
      const src = sourceById[clip.sourceId];
      if (src) extractPeaks(src.url).then((pk) => pk && setPeaks((p) => ({ ...p, [clip.sourceId]: pk })));
    }
    toast.success("Audio détaché — le clip vidéo est passé en muet");
  };

  // ── Auto subtitles: Whisper transcribes the first video's audio ──
  const generateSubtitles = async () => {
    if (!doc.videoClips.length) return toast.error("Importez d'abord une vidéo");
    const src = sourceById[doc.videoClips[0].sourceId];
    if (!src?.file) return toast.error("Source introuvable");

    setTranscribing(true);
    const toastId = toast.loading("Sous-titres — envoi de l'audio…");
    try {
      const { data: job } = await studioTranscribeAPI.start(src.file, "fr", (e) => {
        if (e.total) toast.loading(`Sous-titres — envoi ${Math.round((e.loaded / e.total) * 100)}%`, { id: toastId });
      });
      toast.loading("Sous-titres — transcription Whisper…", { id: toastId });

      let status = job.status;
      let segments = [];
      while (status === "processing") {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: s } = await studioTranscribeAPI.status(job.job_id);
        status = s.status;
        if (status === "failed") throw new Error(s.error || "Transcription échouée");
        if (status === "completed") segments = s.segments || [];
      }
      if (!segments.length) throw new Error("Aucune parole détectée");

      update((d) => ({
        ...d,
        subtitles: segments.map((seg) => ({
          id: uid("sub"), text: seg.text,
          start: seg.start, dur: Math.max(0.2, seg.end - seg.start),
        })),
      }));
      toast.success(`${segments.length} sous-titres générés`, { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Sous-titres impossibles", { id: toastId });
    } finally {
      setTranscribing(false);
    }
  };

  // ── Export: send the timeline + sources, ffmpeg renders it server-side ──
  const handleExport = async () => {
    if (!doc.videoClips.length) return toast.error("Timeline vide");
    setExporting(true);
    const toastId = toast.loading("Export — envoi des médias…");
    try {
      const files = sources.map((s) => s.file);
      const indexOf = Object.fromEntries(sources.map((s, i) => [s.id, i]));

      // Output size: the first video's native size, rounded to even (H.264 requires it)
      const fmt = FORMATS[format];
      const firstEl = videoElsRef.current[doc.videoClips[0].sourceId];
      const [W, H] = fmt.dims
        ? fmt.dims
        : [(firstEl?.videoWidth || 1920) & ~1, (firstEl?.videoHeight || 1080) & ~1];

      // Titles are sized in CSS px against the on-screen player; send a ratio so the
      // server can scale them to the real output height.
      const playerH = playerRef.current?.clientHeight || 720;

      const timeline = {
        width: W, height: H, fps: 30,
        videoClips: doc.videoClips.map((c) => ({
          sourceIndex: indexOf[c.sourceId], in: c.in, out: c.out,
          speed: c.speed, volume: c.volume, muted: !!c.muted,
          fadeIn: c.fadeIn || 0, fadeOut: c.fadeOut || 0,
          transition: c.transition || null,
        })),
        audioClips: doc.audioClips.map((c) => ({
          sourceIndex: indexOf[c.sourceId], in: c.in, dur: c.dur,
          start: c.start, volume: c.volume, muted: !!c.muted,
        })),
        textClips: doc.textClips.map((c) => ({
          text: c.text, start: c.start, dur: c.dur,
          sizeRatio: c.size / playerH, color: c.color,
        })),
        subtitles: (doc.subtitles || []).map((c) => ({
          text: c.text, start: c.start, end: c.start + c.dur,
        })),
      };

      const { data: job } = await studioRenderAPI.start(files, timeline, (e) => {
        if (e.total) toast.loading(`Export — envoi ${Math.round((e.loaded / e.total) * 100)}%`, { id: toastId });
      });

      toast.loading("Export — encodage ffmpeg…", { id: toastId });
      let status = job.status;
      while (status === "processing") {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: s } = await studioRenderAPI.status(job.job_id);
        status = s.status;
        if (status === "failed") throw new Error(s.error || "ffmpeg a échoué");
      }

      const { data: blob } = await studioRenderAPI.download(job.job_id);
      const url = URL.createObjectURL(new Blob([blob], { type: "video/mp4" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `montage_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Montage exporté en MP4", { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Export impossible", { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  // ── Edits ──
  const splitAtPlayhead = () => {
    const t = playheadRef.current;
    const clip = clipAt(t);
    if (!clip) return toast.error("Aucun clip sous la tête de lecture");
    const offset = (t - clip.start) * clip.speed;
    if (offset < 0.05 || clip.in + offset > clip.out - 0.05) return toast.error("Trop près du bord du clip");
    update((d) => {
      const i = d.videoClips.findIndex((c) => c.id === clip.id);
      if (i < 0) return d;
      const left = { ...d.videoClips[i], out: clip.in + offset };
      const right = { ...d.videoClips[i], id: uid("v"), in: clip.in + offset };
      return { ...d, videoClips: [...d.videoClips.slice(0, i), left, right, ...d.videoClips.slice(i + 1)] };
    });
    toast.success("Clip coupé");
  };

  const deleteSelection = () => {
    if (!selection.length) return;
    const ids = new Set(selection.map((s) => s.id));
    update((d) => ({
      ...d,
      videoClips: d.videoClips.filter((c) => !ids.has(c.id)),
      audioClips: d.audioClips.filter((c) => !ids.has(c.id)),
      textClips: d.textClips.filter((c) => !ids.has(c.id)),
      subtitles: (d.subtitles || []).filter((c) => !ids.has(c.id)),
    }));
    setSelection([]);
  };

  const copySelection = () => {
    if (!selection.length) return;
    const d = docRef.current;
    clipboardRef.current = selection.map((s) => {
      const list = s.track === "video" ? d.videoClips : s.track === "audio" ? d.audioClips : d.textClips;
      const clip = list.find((c) => c.id === s.id);
      return clip ? { track: s.track, clip } : null;
    }).filter(Boolean);
    toast.success(`${clipboardRef.current.length} clip(s) copié(s)`);
  };

  const pasteClipboard = () => {
    const items = clipboardRef.current;
    if (!items?.length) return;
    update((d) => {
      const next = { ...d };
      items.forEach(({ track, clip }) => {
        if (track === "video") next.videoClips = [...next.videoClips, { ...clip, id: uid("v") }];
        if (track === "audio") next.audioClips = [...next.audioClips, { ...clip, id: uid("a"), start: playheadRef.current }];
        if (track === "text") next.textClips = [...next.textClips, { ...clip, id: uid("t"), start: playheadRef.current }];
      });
      return next;
    });
    toast.success("Collé");
  };

  const duplicateSelection = () => { copySelection(); setTimeout(pasteClipboard, 0); };

  const addText = () => {
    const clip = { id: uid("t"), text: "Votre titre", start: playheadRef.current, dur: 3, size: 42, color: "#ffffff" };
    update((d) => ({ ...d, textClips: [...d.textClips, clip] }));
    setSelection([{ track: "text", id: clip.id }]);
  };

  const patchClip = (track, id, patch, opts) => {
    const key = track === "video" ? "videoClips"
      : track === "audio" ? "audioClips"
      : track === "subtitle" ? "subtitles"
      : "textClips";
    update((d) => ({ ...d, [key]: d[key].map((c) => (c.id === id ? { ...c, ...patch } : c)) }), opts);
  };

  // ── Pointer helpers ──
  const timelineX = (e) => {
    const el = timelineRef.current;
    if (!el) return 0;
    return e.clientX - el.getBoundingClientRect().left + el.scrollLeft - TRACK_HEADER_W;
  };
  const scrubFromEvent = (e) => seek(Math.max(0, timelineX(e) / pxPerSec));

  const dragWith = (onMove, onDone) => {
    const move = (ev) => onMove(ev);
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      onDone?.();
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const startScrub = (e) => { scrubFromEvent(e); dragWith(scrubFromEvent); };

  const selectClip = (e, track, id) => {
    if (e.shiftKey) {
      setSelection((s) => (s.some((x) => x.id === id) ? s.filter((x) => x.id !== id) : [...s, { track, id }]));
    } else if (!isSelected(id)) {
      setSelection([{ track, id }]);
    }
  };

  const startTrim = (e, clip, edge) => {
    e.stopPropagation();
    pushHistory();
    const startX = e.clientX;
    const orig = { in: clip.in, out: clip.out };
    const src = sourceById[clip.sourceId];
    dragWith((ev) => {
      const delta = ((ev.clientX - startX) / pxPerSec) * clip.speed;
      if (edge === "left") patchClip("video", clip.id, { in: clamp(orig.in + delta, 0, orig.out - 0.1) }, { history: false });
      else patchClip("video", clip.id, { out: clamp(orig.out + delta, orig.in + 0.1, src?.duration ?? orig.out) }, { history: false });
    });
  };

  const startReorder = (e, clip) => {
    e.stopPropagation();
    selectClip(e, "video", clip.id);
    pushHistory();
    dragWith((ev) => {
      const x = timelineX(ev) / pxPerSec;
      update((d) => {
        let acc = 0;
        const centers = d.videoClips.map((c) => {
          const dur = (c.out - c.in) / c.speed;
          const center = acc + dur / 2;
          acc += dur;
          return center;
        });
        const from = d.videoClips.findIndex((c) => c.id === clip.id);
        if (from < 0) return d;
        let to = clamp(centers.filter((c, i) => i !== from && c < x).length, 0, d.videoClips.length - 1);
        if (to === from) return d;
        const next = [...d.videoClips];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return { ...d, videoClips: next };
      }, { history: false });
    });
  };

  const startMove = (e, track, clip, mode) => {
    e.stopPropagation();
    selectClip(e, track, clip.id);
    pushHistory();
    const startX = e.clientX;
    const orig = { start: clip.start, dur: clip.dur };
    dragWith((ev) => {
      const delta = (ev.clientX - startX) / pxPerSec;
      if (mode === "move") {
        patchClip(track, clip.id, { start: Math.max(0, snapSec(orig.start + delta, clip.id)) }, { history: false });
      } else if (mode === "left") {
        const start = clamp(snapSec(orig.start + delta, clip.id), 0, orig.start + orig.dur - 0.2);
        patchClip(track, clip.id, { start, dur: orig.dur - (start - orig.start) }, { history: false });
      } else {
        const end = snapSec(orig.start + orig.dur + delta, clip.id);
        patchClip(track, clip.id, { dur: Math.max(0.2, end - orig.start) }, { history: false });
      }
    });
  };

  // ── Drag & drop from the media library ──
  const onLibraryDragStart = (e, source) => e.dataTransfer.setData("text/source-id", source.id);

  const onTrackDrop = (e, trackKey) => {
    e.preventDefault();
    setDropHint(null);
    const sourceId = e.dataTransfer.getData("text/source-id");
    const source = sourceById[sourceId];
    if (!source) return;
    const dropSec = Math.max(0, timelineX(e) / pxPerSec);

    if (trackKey === "video" && source.kind === "video") {
      update((d) => {
        let acc = 0;
        const centers = d.videoClips.map((c) => {
          const dur = (c.out - c.in) / c.speed;
          const center = acc + dur / 2;
          acc += dur;
          return center;
        });
        const idx = centers.filter((c) => c < dropSec).length;
        const clip = { id: uid("v"), sourceId, in: 0, out: source.duration, speed: 1, volume: 1, muted: false };
        const next = [...d.videoClips];
        next.splice(idx, 0, clip);
        return { ...d, videoClips: next };
      });
    } else if (trackKey === "audio" && source.kind === "audio") {
      update((d) => ({
        ...d,
        audioClips: [...d.audioClips, { id: uid("a"), sourceId, in: 0, start: snapSec(dropSec), dur: source.duration, volume: 1, muted: false }],
      }));
    } else {
      toast.error(source.kind === "audio" ? "Un audio va sur la piste Audio" : "Une vidéo va sur la piste Vidéo");
    }
  };

  // ── Keyboard ──
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); return; }
      if (mod && e.key.toLowerCase() === "c") { e.preventDefault(); copySelection(); return; }
      if (mod && e.key.toLowerCase() === "v") { e.preventDefault(); pasteClipboard(); return; }

      if (e.code === "Space") { e.preventDefault(); isPlaying ? pause() : play(); }
      else if (e.key.toLowerCase() === "s") { e.preventDefault(); splitAtPlayhead(); }
      else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSelection(); }
      else if (e.key === "ArrowLeft") seek(playheadRef.current - (e.shiftKey ? 1 : 1 / 30));
      else if (e.key === "ArrowRight") seek(playheadRef.current + (e.shiftKey ? 1 : 1 / 30));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const selectedClip = useMemo(() => {
    if (!soleSelection) return null;
    const list = soleSelection.track === "video" ? doc.videoClips
      : soleSelection.track === "audio" ? doc.audioClips
      : soleSelection.track === "subtitle" ? (doc.subtitles || [])
      : doc.textClips;
    return list.find((c) => c.id === soleSelection.id) || null;
  }, [soleSelection, doc]);

  const tickStep = pxPerSec > 160 ? 0.5 : pxPerSec > 60 ? 1 : pxPerSec > 24 ? 5 : 15;
  const ticks = [];
  for (let t = 0; t <= Math.max(totalDuration, 10); t += tickStep) ticks.push(t);
  const timelineWidth = Math.max(totalDuration, 10) * pxPerSec + TRACK_HEADER_W + 240;

  const TRACKS = [
    { key: "video", label: "Vidéo principale", icon: Film, color: "#8b5cf6" },
    { key: "audio", label: "Audio", icon: Music, color: "#22c55e" },
    { key: "text", label: "Texte", icon: Type, color: "#f59e0b" },
    { key: "subtitle", label: "Sous-titres", icon: Captions, color: "#38bdf8" },
  ];

  // Filmstrip tiles for one video clip, spaced so they never overlap
  const filmstrip = (c) => {
    const list = thumbs[c.sourceId];
    if (!list) return null;
    const tiles = [];
    let lastX = -Infinity;
    for (const th of list) {
      if (th.t < c.in || th.t > c.out) continue;
      const x = ((th.t - c.in) / c.speed) * pxPerSec;
      if (x - lastX < THUMB_W) continue;
      lastX = x;
      tiles.push(
        <img key={th.t} src={th.url} alt="" draggable={false}
          className="absolute top-0 h-full object-cover opacity-70"
          style={{ left: x, width: THUMB_W }} />
      );
    }
    return tiles;
  };

  return (
    <RecordingStudioLayout>
      <div className="flex flex-col h-[calc(100vh-48px)] bg-[#0f0f14] text-white overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2">
            <Film className="h-4 w-4 text-violet-400" />
            <span className="text-sm font-semibold">Video Editor</span>
            <span className="text-[11px] text-white/30 ml-2">
              {doc.videoClips.length} clip{doc.videoClips.length > 1 ? "s" : ""} · {formatTime(totalDuration)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FORMATS).map(([k, f]) => (
                  <SelectItem key={k} value={k} className="text-xs">{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => fileRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" /> Importer
            </Button>
            <Button size="sm" onClick={handleExport} disabled={exporting || !doc.videoClips.length}
              className="h-8 gap-1.5 text-xs"
              title="Rendu MP4 (H.264 + AAC) côté serveur">
              <Download className="h-3.5 w-3.5" /> {exporting ? "Export…" : "Exporter MP4"}
            </Button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Media library */}
          <aside className="w-56 border-r border-white/5 flex flex-col shrink-0">
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white/40">Médias</div>
            <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
              {sources.length === 0 && (
                <p className="text-[11px] text-white/25 px-2 py-4 text-center">Aucun média importé</p>
              )}
              {sources.map((s) => (
                <div key={s.id} draggable onDragStart={(e) => onLibraryDragStart(e, s)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] cursor-grab active:cursor-grabbing">
                  {s.kind === "audio"
                    ? <Music className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    : <VideoIcon className="h-3.5 w-3.5 text-violet-400 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-[11px] truncate">{s.name}</p>
                    <p className="text-[9px] text-white/30">{formatTime(s.duration)}</p>
                  </div>
                </div>
              ))}
              {sources.length > 0 && (
                <p className="text-[9px] text-white/20 px-2 pt-2">Glissez un média sur une piste</p>
              )}
            </div>
          </aside>

          {/* Player */}
          <main className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 bg-black flex items-center justify-center min-h-0 p-2">
              {/* This framed box is exactly what gets exported. playerRef measures it so
                  title sizes stay proportional between the preview and the render. */}
              <div
                ref={playerRef}
                className="relative bg-black overflow-hidden"
                style={FORMATS[format].css
                  ? { aspectRatio: FORMATS[format].css, height: "100%", maxWidth: "100%" }
                  : { width: "100%", height: "100%" }}
              >
              {sources.filter((s) => s.kind === "video").map((s) => (
                <video key={s.id} ref={(el) => { videoElsRef.current[s.id] = el; }} src={s.url} playsInline
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{ visibility: clipAt(displayTime)?.sourceId === s.id ? "visible" : "hidden" }} />
              ))}
              {/* One <audio> per source: a video's audio can be detached onto the audio track */}
              {sources.map((s) => (
                <audio key={s.id} ref={(el) => { audioElsRef.current[s.id] = el; }} src={s.url} />
              ))}

              {doc.videoClips.length === 0 && (
                <div className="text-center z-10">
                  <Film className="h-12 w-12 text-white/10 mx-auto" />
                  <p className="text-sm text-white/50 mt-3">Aucune vidéo chargée</p>
                  <p className="text-[11px] text-white/25 mt-1">Importez une vidéo pour commencer le montage</p>
                  <Button size="sm" className="mt-4 gap-1.5" onClick={() => fileRef.current?.click()}>
                    <Upload className="h-4 w-4" /> Importer une vidéo
                  </Button>
                </div>
              )}

              {transitionDip > 0 && (
                <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: transitionDip }} />
              )}

              {visibleSubtitle && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-[4%] max-w-[85%] text-center pointer-events-none
                                text-white font-medium px-3 py-1 rounded bg-black/50"
                  style={{ fontSize: "clamp(11px, 2.4vh, 22px)", textShadow: "0 2px 6px rgba(0,0,0,0.9)" }}>
                  {visibleSubtitle.text}
                </div>
              )}

              {visibleTexts.map((t) => (
                <div key={t.id} className="absolute left-1/2 -translate-x-1/2 bottom-[12%] font-bold text-center pointer-events-none px-4"
                  style={{ fontSize: t.size, color: t.color, textShadow: "0 2px 12px rgba(0,0,0,0.8)" }}>
                  {t.text}
                </div>
              ))}
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-2 border-t border-white/5 shrink-0">
              <button onClick={() => seek(0)} className="p-1.5 rounded hover:bg-white/10 text-white/60"><SkipBack className="h-4 w-4" /></button>
              <button onClick={() => (isPlaying ? pause() : play())} disabled={totalDuration === 0}
                className="p-2 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-30">
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <button onClick={() => seek(totalDuration)} className="p-1.5 rounded hover:bg-white/10 text-white/60"><SkipForward className="h-4 w-4" /></button>
              <span className="text-[11px] font-mono text-white/60 tabular-nums">
                {formatTime(displayTime)} / {formatTime(totalDuration)}
              </span>
              <div className="flex items-center gap-2 ml-auto w-40">
                <Volume2 className="h-3.5 w-3.5 text-white/40" />
                <Slider value={[masterVolume]} onValueChange={(v) => setMasterVolume(v[0])} max={100} step={1} />
              </div>
            </div>
          </main>

          {/* Inspector */}
          <aside className="w-64 border-l border-white/5 p-3 space-y-4 overflow-y-auto shrink-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/40">Propriétés</div>
            {selection.length > 1 && <p className="text-[11px] text-white/40">{selection.length} clips sélectionnés</p>}
            {!selectedClip && selection.length <= 1 && <p className="text-[11px] text-white/25">Sélectionnez un clip sur la timeline.</p>}

            {selectedClip && soleSelection.track === "video" && (
              <>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Source</label>
                  <p className="text-[11px] truncate">{sourceById[selectedClip.sourceId]?.name}</p>
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Vitesse</label>
                  <Select value={String(selectedClip.speed)} onValueChange={(v) => patchClip("video", selectedClip.id, { speed: Number(v) })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0.25, 0.5, 1, 1.5, 2, 4].map((s) => <SelectItem key={s} value={String(s)} className="text-xs">{s}x</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Volume {Math.round(selectedClip.volume * 100)}%</label>
                  <Slider value={[selectedClip.volume * 100]} max={100} step={1}
                    onValueChange={(v) => patchClip("video", selectedClip.id, { volume: v[0] / 100 }, { history: false })} />
                </div>
                <button onClick={() => patchClip("video", selectedClip.id, { muted: !selectedClip.muted })}
                  className={`w-full flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs ${
                    selectedClip.muted ? "bg-rose-500/20 text-rose-400" : "bg-white/5 text-white/60"}`}>
                  {selectedClip.muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                  {selectedClip.muted ? "Muet" : "Son actif"}
                </button>
                <button onClick={() => detachAudio(selectedClip)}
                  className="w-full flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">
                  <AudioLines className="h-3.5 w-3.5" /> Détacher l'audio
                </button>

                <div className="pt-2 border-t border-white/5">
                  <label className="text-[10px] text-white/40 block mb-1">Fondu audio ouverture {(selectedClip.fadeIn || 0).toFixed(1)}s</label>
                  <Slider value={[(selectedClip.fadeIn || 0) * 10]} max={30} step={1}
                    onValueChange={(v) => patchClip("video", selectedClip.id, { fadeIn: v[0] / 10 }, { history: false })} />
                  <label className="text-[10px] text-white/40 block mb-1 mt-3">Fondu audio fermeture {(selectedClip.fadeOut || 0).toFixed(1)}s</label>
                  <Slider value={[(selectedClip.fadeOut || 0) * 10]} max={30} step={1}
                    onValueChange={(v) => patchClip("video", selectedClip.id, { fadeOut: v[0] / 10 }, { history: false })} />
                </div>

                {doc.videoClips.findIndex((c) => c.id === selectedClip.id) > 0 && (
                  <div className="pt-2 border-t border-white/5">
                    <label className="text-[10px] text-white/40 block mb-1">Transition avec le clip précédent</label>
                    <Select value={selectedClip.transition?.type || "none"}
                      onValueChange={(v) => patchClip("video", selectedClip.id, { transition: { type: v, dur: selectedClip.transition?.dur || 0.5 } })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TRANSITIONS.map((t) => <SelectItem key={t.v} value={t.v} className="text-xs">{t.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {selectedClip.transition?.type && selectedClip.transition.type !== "none" && (
                      <>
                        <label className="text-[10px] text-white/40 block mb-1 mt-3">Durée {(selectedClip.transition.dur || 0.5).toFixed(1)}s</label>
                        <Slider value={[(selectedClip.transition.dur || 0.5) * 10]} min={2} max={30} step={1}
                          onValueChange={(v) => patchClip("video", selectedClip.id,
                            { transition: { ...selectedClip.transition, dur: v[0] / 10 } }, { history: false })} />
                        <p className="text-[9px] text-white/25 mt-2 leading-relaxed">
                          L'aperçu simule la transition par un fondu au noir. Le fichier exporté contient le vrai fondu enchaîné.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {selectedClip && soleSelection.track === "text" && (
              <>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Texte</label>
                  <Input value={selectedClip.text} className="h-8 text-xs"
                    onChange={(e) => patchClip("text", selectedClip.id, { text: e.target.value }, { history: false })} />
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Taille {selectedClip.size}px</label>
                  <Slider value={[selectedClip.size]} min={12} max={120} step={1}
                    onValueChange={(v) => patchClip("text", selectedClip.id, { size: v[0] }, { history: false })} />
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Couleur</label>
                  <div className="flex gap-1.5">
                    {["#ffffff", "#f43f5e", "#22c55e", "#f59e0b", "#3b82f6", "#a855f7"].map((c) => (
                      <button key={c} onClick={() => patchClip("text", selectedClip.id, { color: c })}
                        className={`h-6 w-6 rounded-full border-2 ${selectedClip.color === c ? "border-white" : "border-transparent"}`}
                        style={{ background: c }} />
                    ))}
                  </div>
                </div>
              </>
            )}

            {selectedClip && soleSelection.track === "subtitle" && (
              <div>
                <label className="text-[10px] text-white/40 block mb-1">Sous-titre</label>
                <Input value={selectedClip.text} className="h-8 text-xs"
                  onChange={(e) => patchClip("subtitle", selectedClip.id, { text: e.target.value }, { history: false })} />
                <p className="text-[9px] text-white/25 mt-2">{formatTime(selectedClip.start)} → {formatTime(selectedClip.start + selectedClip.dur)}</p>
              </div>
            )}

            {selectedClip && soleSelection.track === "audio" && (
              <div>
                <label className="text-[10px] text-white/40 block mb-1">Volume {Math.round(selectedClip.volume * 100)}%</label>
                <Slider value={[selectedClip.volume * 100]} max={100} step={1}
                  onValueChange={(v) => patchClip("audio", selectedClip.id, { volume: v[0] / 100 }, { history: false })} />
              </div>
            )}
          </aside>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-1 px-3 py-1.5 border-t border-white/5 shrink-0">
          <button onClick={undo} disabled={!canUndo} className="p-1.5 rounded hover:bg-white/10 text-white/60 disabled:opacity-25" title="Annuler (Ctrl+Z)">
            <Undo2 className="h-4 w-4" />
          </button>
          <button onClick={redo} disabled={!canRedo} className="p-1.5 rounded hover:bg-white/10 text-white/60 disabled:opacity-25" title="Rétablir (Ctrl+Maj+Z)">
            <Redo2 className="h-4 w-4" />
          </button>
          <div className="w-px h-5 bg-white/10 mx-1" />
          <button onClick={splitAtPlayhead} className="p-1.5 rounded hover:bg-white/10 text-white/60" title="Couper (S)"><Scissors className="h-4 w-4" /></button>
          <button onClick={deleteSelection} disabled={!selection.length} className="p-1.5 rounded hover:bg-white/10 text-white/60 disabled:opacity-25" title="Supprimer (Suppr)"><Trash2 className="h-4 w-4" /></button>
          <button onClick={duplicateSelection} disabled={!selection.length} className="p-1.5 rounded hover:bg-white/10 text-white/60 disabled:opacity-25" title="Dupliquer (Ctrl+C / Ctrl+V)"><Copy className="h-4 w-4" /></button>
          <div className="w-px h-5 bg-white/10 mx-1" />
          <button onClick={addText} className="p-1.5 rounded hover:bg-white/10 text-white/60" title="Ajouter un texte"><Type className="h-4 w-4" /></button>
          <button onClick={generateSubtitles} disabled={transcribing || !doc.videoClips.length}
            className="p-1.5 rounded hover:bg-white/10 text-white/60 disabled:opacity-25"
            title="Sous-titres automatiques (Whisper)">
            <Captions className={`h-4 w-4 ${transcribing ? "animate-pulse text-violet-400" : ""}`} />
          </button>
          <button onClick={() => setSnapping((s) => !s)}
            className={`p-1.5 rounded ${snapping ? "bg-violet-500/20 text-violet-300" : "hover:bg-white/10 text-white/60"}`}
            title={snapping ? "Aimant activé" : "Aimant désactivé"}>
            <Magnet className="h-4 w-4" />
          </button>

          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setPxPerSec((z) => clamp(z / 1.4, MIN_PX_PER_SEC, MAX_PX_PER_SEC))} className="p-1.5 rounded hover:bg-white/10 text-white/60"><ZoomOut className="h-4 w-4" /></button>
            <span className="text-[10px] font-mono text-white/40 w-16 text-center">{Math.round(pxPerSec)} px/s</span>
            <button onClick={() => setPxPerSec((z) => clamp(z * 1.4, MIN_PX_PER_SEC, MAX_PX_PER_SEC))} className="p-1.5 rounded hover:bg-white/10 text-white/60"><ZoomIn className="h-4 w-4" /></button>
            <button onClick={() => {
              const w = (timelineRef.current?.clientWidth || 800) - TRACK_HEADER_W - 40;
              setPxPerSec(clamp(w / Math.max(totalDuration, 1), MIN_PX_PER_SEC, MAX_PX_PER_SEC));
            }} className="p-1.5 rounded hover:bg-white/10 text-white/60" title="Ajuster à la fenêtre">
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="h-[200px] overflow-x-auto overflow-y-hidden border-t border-white/5 shrink-0 relative select-none">
          <div style={{ width: timelineWidth }} className="relative">
            <div className="h-7 bg-[#14141b] border-b border-white/5 cursor-text relative"
              style={{ marginLeft: TRACK_HEADER_W }} onMouseDown={startScrub}>
              {ticks.map((t) => (
                <div key={t} className="absolute top-0 h-full" style={{ left: t * pxPerSec }}>
                  <div className="w-px h-2 bg-white/20" />
                  <span className="text-[9px] text-white/30 ml-1 font-mono">{formatTime(t).slice(0, 5)}</span>
                </div>
              ))}
            </div>

            <div ref={playheadElRef} className="absolute top-0 bottom-0 w-px bg-violet-500 z-20 pointer-events-none" style={{ left: TRACK_HEADER_W }}>
              <div className="w-3 h-3 -ml-1.5 rounded-sm bg-violet-500" />
            </div>

            {TRACKS.map((track) => (
              <div key={track.key} className="flex h-[52px] border-b border-white/5">
                <div className="flex items-center gap-1.5 px-3 shrink-0 bg-[#14141b] border-r border-white/5 sticky left-0 z-10" style={{ width: TRACK_HEADER_W }}>
                  <track.icon className="h-3 w-3" style={{ color: track.color }} />
                  <span className="text-[10px] text-white/50 truncate">{track.label}</span>
                </div>

                <div className={`relative flex-1 ${dropHint === track.key ? "bg-violet-500/10" : ""}`}
                  onMouseDown={() => setSelection([])}
                  onDragOver={(e) => { e.preventDefault(); setDropHint(track.key); }}
                  onDragLeave={() => setDropHint(null)}
                  onDrop={(e) => onTrackDrop(e, track.key)}
                >
                  {track.key === "audio" && doc.audioClips.length === 0 && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-white/15">Glissez un fichier audio ici</span>
                  )}
                  {track.key === "text" && doc.textClips.length === 0 && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-white/15">Bouton T pour ajouter un titre</span>
                  )}
                  {track.key === "subtitle" && !(doc.subtitles || []).length && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-white/15">Bouton sous-titres pour transcrire l'audio</span>
                  )}

                  {track.key === "video" && videoLayout.map((c) => (
                    <div key={c.id} onMouseDown={(e) => startReorder(e, c)}
                      className={`absolute top-1.5 bottom-1.5 rounded-md overflow-hidden cursor-grab active:cursor-grabbing border ${
                        isSelected(c.id) ? "border-violet-400 ring-1 ring-violet-400/40" : "border-white/10"}`}
                      style={{ left: c.start * pxPerSec, width: Math.max(6, c.dur * pxPerSec), background: "linear-gradient(180deg,#4c1d95,#5b21b6)" }}>
                      <div className="absolute inset-0 overflow-hidden">{filmstrip(c)}</div>
                      <div onMouseDown={(e) => startTrim(e, c, "left")} className="absolute left-0 top-0 bottom-0 w-1.5 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <div onMouseDown={(e) => startTrim(e, c, "right")} className="absolute right-0 top-0 bottom-0 w-1.5 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <span className="relative text-[9px] text-white px-2.5 leading-[38px] truncate block drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                        {sourceById[c.sourceId]?.name} {c.speed !== 1 && `· ${c.speed}x`}
                      </span>
                    </div>
                  ))}

                  {track.key === "audio" && doc.audioClips.map((c) => (
                    <div key={c.id} onMouseDown={(e) => startMove(e, "audio", c, "move")}
                      className={`absolute top-1.5 bottom-1.5 rounded-md overflow-hidden cursor-grab border ${
                        isSelected(c.id) ? "border-emerald-300" : "border-white/10"}`}
                      style={{ left: c.start * pxPerSec, width: Math.max(6, c.dur * pxPerSec), background: "linear-gradient(180deg,#065f46,#047857)" }}>
                      <Waveform peaks={peaks[c.sourceId]} width={Math.max(6, c.dur * pxPerSec)} />
                      <div onMouseDown={(e) => startMove(e, "audio", c, "left")} className="absolute left-0 top-0 bottom-0 w-1.5 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <div onMouseDown={(e) => startMove(e, "audio", c, "right")} className="absolute right-0 top-0 bottom-0 w-1.5 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <span className="relative text-[9px] text-white/90 px-2.5 leading-[38px] truncate block">{sourceById[c.sourceId]?.name}</span>
                    </div>
                  ))}

                  {track.key === "text" && doc.textClips.map((c) => (
                    <div key={c.id} onMouseDown={(e) => startMove(e, "text", c, "move")}
                      className={`absolute top-1.5 bottom-1.5 rounded-md cursor-grab border ${
                        isSelected(c.id) ? "border-amber-300" : "border-white/10"}`}
                      style={{ left: c.start * pxPerSec, width: Math.max(6, c.dur * pxPerSec), background: "linear-gradient(180deg,#92400e,#b45309)" }}>
                      <div onMouseDown={(e) => startMove(e, "text", c, "left")} className="absolute left-0 top-0 bottom-0 w-1.5 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <div onMouseDown={(e) => startMove(e, "text", c, "right")} className="absolute right-0 top-0 bottom-0 w-1.5 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <span className="text-[9px] text-white/90 px-2.5 leading-[38px] truncate block">{c.text}</span>
                    </div>
                  ))}

                  {track.key === "subtitle" && (doc.subtitles || []).map((c) => (
                    <div key={c.id} onMouseDown={(e) => startMove(e, "subtitle", c, "move")}
                      className={`absolute top-1.5 bottom-1.5 rounded-md cursor-grab border ${
                        isSelected(c.id) ? "border-sky-300" : "border-white/10"}`}
                      style={{ left: c.start * pxPerSec, width: Math.max(4, c.dur * pxPerSec), background: "linear-gradient(180deg,#075985,#0369a1)" }}>
                      <div onMouseDown={(e) => startMove(e, "subtitle", c, "left")} className="absolute left-0 top-0 bottom-0 w-1 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <div onMouseDown={(e) => startMove(e, "subtitle", c, "right")} className="absolute right-0 top-0 bottom-0 w-1 bg-white/40 hover:bg-white cursor-ew-resize z-10" />
                      <span className="text-[9px] text-white/90 px-2 leading-[38px] truncate block">{c.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <input ref={fileRef} type="file" accept="video/*,audio/*" multiple className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      </div>
    </RecordingStudioLayout>
  );
}
